import React, { useEffect, useMemo, useState } from 'react'
import { Button, Grid2 } from '@mui/material'
import { GRAY_COLORS, INFO_COLORS } from 'asterX-UI'
import { AddBoxOutlined, AddCircleOutline } from '@mui/icons-material'
import { useAuthStore } from 'context/AuthStore'
import { useHomeStore } from 'context/HomeStore'
import CreateLoanApplicationModal from 'components/ClientModal/CreateLoanApplicationModal'
import { DICT_ADMIN, HEAD_UNDERWRITER, SUPER_UNDERWRITER, UNDERWRITER } from 'lib/constants/roles'
import { useNavigate } from 'react-router'
import { getUserTabs } from './header-constant'
import { TimerUnderwriterStatus } from './components/TimerUnderwriterStatus'

const Header = () => {
  const navigate = useNavigate()
  const { authStore } = useAuthStore()
  const {
    homeStore: { activeApplication, tab },
    homeStoreFunc
  } = useHomeStore()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hasLoanApplications, setHasLoanApplications] = useState(false)

  const tabsByRole = useMemo(() => {
    return getUserTabs(authStore.isUserRole)
  }, [authStore.isUserRole])

  const handleOpenModal = () => setIsModalOpen(true)

  const handleAssignByRole = () => {
    if (hasLoanApplications) {
      homeStoreFunc.assignByRole()
    }
  }

  const getApplicationsList = async data => {
    try {
      const response = await homeStoreFunc.getApplicationsList(data)
      setHasLoanApplications(response?.list?.length > 0)
      homeStoreFunc.setCountFiltered(response?.count)
    } catch (error) {
      console.error('Error fetching applications:', error)
    }
  }

  const handleNavigate = () => {
    navigate(`/application/${activeApplication?.id}`)
  }

  useEffect(() => {
    if (!activeApplication?.id) {
      let data = {
        hasAssign: false
      }
      switch (authStore.isUserRole) {
        case UNDERWRITER:
          data = {
            ...data,
            statusCodeList: ['userUnderwriterDecision', 'userCheckCollateral', 'userCheckBankDocs']
            // sumTo: 20000000
          }
          break
        case HEAD_UNDERWRITER:
          data = {
            ...data,
            statusCodeList: [
              'userUnderwriterDecision',
              'userCheckCollateral',
              'userCheckBankDocs',
              'userHeadUnderwriterDecision'
            ],
            sumFrom: 19999999
          }
          break
        case SUPER_UNDERWRITER:
          data = {
            ...data,
            statusCodeList: ['userUnderwriterDecision', 'userHeadUnderwriterDecision']
          }
          break
        /*TODO - case CREDIT_ADMIN:
          data = { ...data, statusCodeList: ['userCheckCollateral'] }
          break*/
        default:
          return
      }
      const fetchData = () => getApplicationsList(data)
      fetchData()
      const intervalId = setInterval(fetchData, 10000)

      return () => clearInterval(intervalId)
    }
  }, [authStore.isUserRole, activeApplication?.id])

  const getButtonConfig = role => {
    const baseConfig = {
      variant: 'outlined',
      text: 'Заявок нет',
      onClick: null,
      disabled: true,
      sx: {
        fontSize: 14,
        fontWeight: 600,
        color: GRAY_COLORS[500],
        borderColor: GRAY_COLORS[300]
      }
    }

    if (activeApplication?.id && [UNDERWRITER, HEAD_UNDERWRITER, SUPER_UNDERWRITER].includes(role)) {
      return {
        variant: 'outlined',
        text: <TimerUnderwriterStatus data={activeApplication} />,
        onClick: handleNavigate,
        disabled: false,
        sx: {
          fontSize: 14,
          fontWeight: 600
        }
      }
    }

    if (hasLoanApplications) {
      if ([UNDERWRITER, HEAD_UNDERWRITER, SUPER_UNDERWRITER].includes(role)) {
        return {
          variant: 'contained',
          text: role === UNDERWRITER ? 'Приступить к работе' : 'Принять в работу',
          onClick: handleAssignByRole,
          disabled: false,
          sx: {
            fontSize: 14,
            fontWeight: 600
          }
        }
      }
    }
    if(role === DICT_ADMIN) {
      const createLabels = {
        groups:   'Создать группу',
        dealers:  'Создать дилера',
        locations:'Создать локацию'
      };
      return {
        variant: 'contained',
        startIcon:<AddCircleOutline />,
        text:createLabels[tab] || 'Создать',
        onClick: () => homeStoreFunc.openAdminModal('create', tab, {}),
        disabled: false,
        sx: {
          fontSize: 14,
          fontWeight: 600
        }
      }
    }

    return baseConfig
  }

  const roleButtonConfig = useMemo(() => {
    return {
      MANAGER: {
        variant: 'outlined',
        startIcon: <AddBoxOutlined />,
        text: 'Создать заявку',
        sx: {
          borderColor: GRAY_COLORS[300],
          color: GRAY_COLORS[800],
          fontSize: 14,
          fontWeight: 600,
          boxShadow: 'none'
        },
        onClick: handleOpenModal
      },
      UNDERWRITER: getButtonConfig(UNDERWRITER),
      HEAD_UNDERWRITER: getButtonConfig(HEAD_UNDERWRITER),
      SUPER_UNDERWRITER: getButtonConfig(SUPER_UNDERWRITER),
      DICT_ADMIN: getButtonConfig(DICT_ADMIN)
    }
  }, [activeApplication?.id, hasLoanApplications,tab])

  const renderRoleButton = () => {
    const roleConfig = roleButtonConfig[authStore.isUserRole]
    if (!roleConfig) return null

    return (
      <Button
        variant={roleConfig.variant}
        startIcon={roleConfig.startIcon}
        endIcon={roleConfig.endIcon}
        sx={roleConfig.sx}
        onClick={roleConfig.onClick}
      >
        {roleConfig.text}
      </Button>
    )
  }

  return (
    <>
      <Grid2
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          padding: '12px 16px',
          borderBottom: `1px solid ${GRAY_COLORS[200]}`
        }}
      >
        <Grid2 sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {tabsByRole.map(item => (
            <Button
              key={item.code}
              sx={{
                padding: '10px 14px',
                fontSize: 14,
                fontWeight: 600,
                color: tab === item.code ? INFO_COLORS[700] : GRAY_COLORS[500],
                backgroundColor: tab === item.code ? INFO_COLORS[50] : 'transparent',
                '&:hover': { backgroundColor: tab === item.code ? INFO_COLORS[100] : 'transparent' }
              }}
              onClick={() => homeStoreFunc.handleChangeTabs(item.code)}
            >
              {item?.label}
            </Button>
          ))}
        </Grid2>

        {renderRoleButton()}
      </Grid2>

      <CreateLoanApplicationModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  )
}

export default Header
